import { useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Wrench,
  ChevronDown,
  ChevronRight,
  Loader2,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { ValidationResult, ValidatedRow } from "@/types/import";

interface PreviewStepProps {
  validationResult: ValidationResult;
  onBackToMapping: () => void;
  onDiscard: () => void;
  onExecute: () => void;
  isImporting?: boolean;
  isResetting?: boolean;
  isDiscarding?: boolean;
  importDone?: boolean;
}

function SummaryCard({
  label,
  count,
  color,
  icon: Icon,
}: {
  label: string;
  count: number;
  color: "green" | "red" | "yellow" | "blue";
  icon: React.ComponentType<{ className?: string }>;
}) {
  const styles = {
    green: "border-green-200 bg-green-50 text-green-700",
    red: "border-red-200 bg-red-50 text-red-700",
    yellow: "border-yellow-200 bg-yellow-50 text-yellow-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
  };

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-lg border px-4 py-3",
        styles[color],
      )}
    >
      <Icon className="h-5 w-5 shrink-0" />
      <div>
        <p className="text-2xl font-bold">{count}</p>
        <p className="text-sm">{label}</p>
      </div>
    </div>
  );
}

function RowDetail({ row }: { row: ValidatedRow }) {
  const [isOpen, setIsOpen] = useState(false);

  const hasDetails =
    row.blockingErrors.length > 0 ||
    row.warnings.length > 0 ||
    row.autoFixes.length > 0;

  if (!hasDetails) return null;

  return (
    <tr>
      <td colSpan={7} className="px-4 pb-3">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          {isOpen ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronRight className="h-3 w-3" />
          )}
          {row.blockingErrors.length +
            row.warnings.length +
            row.autoFixes.length}{" "}
          Hinweise
        </button>
        {isOpen && (
          <div className="mt-2 space-y-1">
            {row.blockingErrors.map((e, i) => (
              <div key={`err-${i}`} className="flex items-start gap-2 text-xs">
                <span className="inline-flex shrink-0 items-center rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700">
                  Fehler
                </span>
                <span>
                  {e.field}: {e.message}
                </span>
              </div>
            ))}
            {row.warnings.map((w, i) => (
              <div key={`warn-${i}`} className="flex items-start gap-2 text-xs">
                <span className="inline-flex shrink-0 items-center rounded-full bg-yellow-100 px-1.5 py-0.5 text-[10px] font-medium text-yellow-700">
                  Warnung
                </span>
                <span>
                  {w.field}: {w.message}
                </span>
              </div>
            ))}
            {row.autoFixes.map((f, i) => (
              <div key={`fix-${i}`} className="flex items-start gap-2 text-xs">
                <span className="inline-flex shrink-0 items-center rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
                  Korrektur
                </span>
                <span>
                  {f.field}: {f.message}
                </span>
              </div>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
}

function StatusIndicator({ status }: { status: ValidatedRow["status"] }) {
  if (status === "error") {
    return <XCircle className="h-4 w-4 text-red-500" />;
  }
  if (status === "warning") {
    return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
  }
  return <CheckCircle className="h-4 w-4 text-green-500" />;
}

export function PreviewStep({
  validationResult,
  onBackToMapping,
  onDiscard,
  onExecute,
  isImporting = false,
  isResetting = false,
  isDiscarding = false,
  importDone = false,
}: PreviewStepProps) {
  const [showOnlyProblems, setShowOnlyProblems] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const busy = isImporting || isResetting || isDiscarding;

  const { totalRows, validRows, errorRows, warningRows, autoFixCount, rows } =
    validationResult;

  const hasProblems = errorRows > 0 || warningRows > 0;
  const allRowsError = errorRows === totalRows;

  const displayedRows = showOnlyProblems
    ? rows.filter((r) => r.status !== "valid")
    : rows;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Vorschau &amp; Validierung</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {totalRows} Zeilen analysiert — Ergebnis der Drei-Klassen-Validierung
        </p>
      </div>

      {/* Summary Banner */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <SummaryCard
          label="Gültige Zeilen"
          count={validRows}
          color="green"
          icon={CheckCircle}
        />
        {errorRows > 0 && (
          <SummaryCard
            label="Fehlerhafte Zeilen"
            count={errorRows}
            color="red"
            icon={XCircle}
          />
        )}
        {warningRows > 0 && (
          <SummaryCard
            label="Warnungen"
            count={warningRows}
            color="yellow"
            icon={AlertTriangle}
          />
        )}
        {autoFixCount > 0 && (
          <SummaryCard
            label="Auto-Korrekturen"
            count={autoFixCount}
            color="blue"
            icon={Wrench}
          />
        )}
      </div>

      {/* Filter Toggle */}
      {hasProblems && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={showOnlyProblems}
            onChange={(e) => setShowOnlyProblems(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          Nur Problemzeilen anzeigen
        </label>
      )}

      {/* Preview Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium">Zeile</th>
                  <th className="px-4 py-3 text-center font-medium">Status</th>
                  <th className="px-4 py-3 text-left font-medium">SKU</th>
                  <th className="px-4 py-3 text-left font-medium">
                    Produktname
                  </th>
                  <th className="px-4 py-3 text-right font-medium">Preis</th>
                  <th className="px-4 py-3 text-left font-medium">Tags</th>
                </tr>
              </thead>
              <tbody>
                {displayedRows.map((row) => (
                  <>
                    <tr
                      key={row.rowIndex}
                      className={cn(
                        "border-b last:border-b-0",
                        row.status === "error" && "bg-red-50",
                        row.status === "warning" && "bg-yellow-50",
                      )}
                    >
                      <td className="px-4 py-3 font-mono text-xs">
                        {row.rowIndex + 1}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <StatusIndicator status={row.status} />
                      </td>
                      <td className="max-w-[120px] truncate px-4 py-3 font-mono text-xs">
                        {row.transformedData.external_key ?? "—"}
                      </td>
                      <td className="max-w-[200px] truncate px-4 py-3">
                        {row.transformedData.title ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs">
                        {row.transformedData.price ?? "—"}
                      </td>
                      <td className="max-w-[150px] truncate px-4 py-3 text-xs">
                        {row.transformedData.tags ?? "—"}
                      </td>
                    </tr>
                    <RowDetail key={`detail-${row.rowIndex}`} row={row} />
                  </>
                ))}
                {displayedRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-8 text-center text-muted-foreground"
                    >
                      Keine Zeilen zum Anzeigen
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Error hint */}
      {errorRows > 0 && !allRowsError && (
        <p className="text-sm text-muted-foreground">
          {errorRows} Zeile{errorRows > 1 ? "n" : ""} mit Fehlern{" "}
          {errorRows > 1 ? "werden" : "wird"} beim Import übersprungen.
        </p>
      )}

      {/* Discard confirmation hint (two-click inline confirm) */}
      {confirmDiscard && (
        <p className="text-sm text-muted-foreground">
          Der hochgeladene Import und die Spalten-Zuordnung gehen verloren.
        </p>
      )}

      {/* Buttons */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onBackToMapping} disabled={busy}>
            {isResetting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ArrowLeft className="mr-2 h-4 w-4" />
            )}
            Zurück zum Mapping
          </Button>
          {confirmDiscard ? (
            <Button variant="destructive" onClick={onDiscard} disabled={busy}>
              {isDiscarding ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              Wirklich verwerfen?
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => setConfirmDiscard(true)}
              disabled={busy}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Import verwerfen
            </Button>
          )}
        </div>
        <Button
          onClick={onExecute}
          disabled={allRowsError || busy || importDone}
        >
          {isImporting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Import läuft…
            </>
          ) : importDone ? (
            <>
              <CheckCircle className="mr-2 h-4 w-4" />
              Import abgeschlossen
            </>
          ) : (
            <>
              <ArrowRight className="mr-2 h-4 w-4" />
              Import starten
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
